import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Star, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_AVATAR_URL, getInstrumentAvatarUrl } from '../../shared/avatars';

interface MusicianCardProps {
  id: string;
  name: string;
  instrument: string;
  skillLevel: string;
  location: string;
  distance: number | null;
  age?: number | null;
  imageUrl?: string;
}

const MusicianCard: React.FC<MusicianCardProps> = ({
  id,
  name,
  instrument,
  skillLevel,
  location,
  distance,
  age,
  imageUrl,
}) => {
  const navigate = useNavigate();

  // Function to determine the skill level stars
  const getSkillStars = (level: string) => {
    switch (level) {
      case 'Beginner':
        return 1;
      case 'Intermediate':
        return 2;
      case 'Advanced':
        return 3;
      case 'Professional':
        return 4;
      default:
        return 0;
    }
  };

  const skillStars = getSkillStars(skillLevel);
  const avatarUrl = getInstrumentAvatarUrl(instrument);
  const handleAvatarError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = DEFAULT_AVATAR_URL;
    event.currentTarget.onerror = null;
  };

  const handleCardClick = () => {
    navigate(`/profile/${id}`);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`/profile/${id}`);
    }
  };

  const handleContactClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate('/messages', {
      state: {
        recipientId: id,
        recipientName: name,
        recipientInstrument: instrument,
      },
    });
  };

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      className="bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <div className="relative aspect-square bg-gray-700 overflow-hidden">
        <img
          src={imageUrl || avatarUrl}
          alt={`${name} - ${instrument}`}
          onError={handleAvatarError}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/35 to-black/70" />
        <div className="absolute inset-0 p-4 flex flex-col justify-between text-white">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-lg font-bold leading-tight">{name}</h3>
              <span className="inline-flex mt-1 bg-purple-600 text-white text-xs font-medium px-2 py-1 rounded">
                {instrument}
              </span>
            </div>
            <div className="flex flex-col items-end">
              {age !== null && age !== undefined && (
                <span className="bg-purple-600 text-white text-xs font-semibold px-2 py-1 rounded">
                  Age {age}
                </span>
              )}
              <div className="flex items-center mt-1">
                <div className="flex mr-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < skillStars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-100">{skillLevel}</span>
              </div>
            </div>
          </div>

          <div>
            <div className="text-gray-100 text-sm mb-3">
              <div className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                <span className="truncate">{location}</span>
              </div>
              <span className="block ml-5 text-xs text-gray-200">
                {distance === null ? 'Distance N/A' : `${distance} km away`}
              </span>
            </div>

            <button
              onClick={handleContactClick}
              className="w-full flex items-center justify-center py-2 px-3 bg-purple-600/90 hover:bg-purple-600 text-white rounded transition-colors duration-200 text-sm"
            >
              <Mail className="w-4 h-4 mr-1" /> Contattami
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MusicianCard;
